import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  WEEKDAY_LABELS_SHORT,
  assignLanes,
  formatTime,
  isAllDayEvent,
  isSameDay,
  startOfDay,
  type CalendarEvent,
} from '../lib/calendar-utils.js';
import { Card, CardBody } from './ui/card.js';

/**
 * Google-Calendar-style hour grid. 24 hours tall, scrollable, defaults to
 * ~7am scroll position. Used by both Day view (1 column) and Week view (7).
 *
 * Drag interactions:
 *  - Drag the chip body → reschedule (HTML5 DnD; column drop position
 *    determines new start_at, time-of-day captured from clientY).
 *  - Drag the bottom-edge handle → resize end_at via mousedown/mousemove
 *    /mouseup. Native DnD can't easily distinguish edge vs body, so the
 *    resize gesture stops drag-event propagation and tracks pointer
 *    deltas manually.
 *
 * 15-minute snap on both reschedule and resize. Min duration 15 minutes.
 */

const HOUR_HEIGHT = 48; // px per hour
const TOTAL_HEIGHT = HOUR_HEIGHT * 24;
const TIME_AXIS_WIDTH = 64; // px
const SNAP_MIN = 15;
const MIN_DURATION_MIN = 15;
const DEFAULT_SCROLL_HOUR = 7;

const DRAG_MIME = 'application/x-xsc-event';

interface DragPayload {
  id: string;
  start_at: string;
}

interface CalendarEventLite extends CalendarEvent {
  attendee_name?: string;
  attendee_phone?: string;
}

const STATUS_TONE: Record<string, string> = {
  pending:   'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-blue-400 dark:border-blue-600',
  awaiting:  'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-400 dark:border-amber-600',
  confirmed: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border-green-400 dark:border-green-600',
  failed:    'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-400 dark:border-red-600',
};

export interface TimeGridProps<E extends CalendarEventLite> {
  days: Date[]; // 1 for Day view, 7 for Week view
  events: E[];
  /** Body drag dropped at a specific time on a target day. */
  onDropToTime: (eventId: string, sourceISO: string, targetTime: Date) => void;
  /** Bottom-edge drag committed a new end_at. */
  onResize: (eventId: string, newEnd: Date) => void;
  /** Top-edge drag committed a new start_at. */
  onResizeStart: (eventId: string, newStart: Date) => void;
  /** Click on empty grid area → quick-create gesture. Time grid passes
   * (start, start+30min); all-day row passes (midnight, midnight+24h). */
  onCreateAt: (start: Date, end: Date) => void;
}

export function TimeGridView<E extends CalendarEventLite>({
  days,
  events,
  onDropToTime,
  onResize,
  onResizeStart,
  onCreateAt,
}: TimeGridProps<E>): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState<Date>(() => new Date());
  const [dragHover, setDragHover] = useState<{ dayISO: string; minutes: number } | null>(null);

  // Default scroll to 7am on mount; preserves user's scroll position thereafter.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
    }
  }, []);

  // Now-line refresh — once a minute is plenty.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Partition: events ≥ 24h go in the all-day row; the rest go in the
  // time grid (where the EventBlock clamps anything spilling outside the
  // [0, 24h] range of a single day).
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay: E[] = [];
    const timed: E[] = [];
    for (const e of events) {
      if (isAllDayEvent(e)) allDay.push(e);
      else timed.push(e);
    }
    return { allDayEvents: allDay, timedEvents: timed };
  }, [events]);

  const gridTemplateColumns = `${TIME_AXIS_WIDTH}px repeat(${days.length}, 1fr)`;

  return (
    <Card>
      <CardBody className="p-0">
        {/* Day-headers strip (sticky outside the scroll area) */}
        <div
          className="grid border-b border-border bg-surface-alt"
          style={{ gridTemplateColumns }}
        >
          <div />
          {days.map((day) => {
            const isToday = isSameDay(day, startOfDay(now));
            return (
              <div
                key={day.toISOString()}
                className="px-2 py-2 text-center border-l border-border"
              >
                <div className="text-[10px] uppercase tracking-wide text-ink-muted">
                  {WEEKDAY_LABELS_SHORT[day.getDay()]}
                </div>
                <div
                  className={`text-lg font-semibold mt-0.5 inline-flex items-center justify-center min-w-7 h-7 rounded-full ${
                    isToday ? 'bg-accent text-accent-fg px-2' : ''
                  }`}
                >
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* All-day row — only renders if there are events to show */}
        {allDayEvents.length > 0 && (
          <AllDayRow
            days={days}
            events={allDayEvents}
            gridTemplateColumns={gridTemplateColumns}
            onCreateAtDay={(day) => {
              const start = startOfDay(day);
              const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
              onCreateAt(start, end);
            }}
          />
        )}

        {/* Scrollable grid body */}
        <div ref={scrollRef} className="overflow-y-auto max-h-[70vh]">
          <div
            className="grid relative"
            style={{ gridTemplateColumns, height: TOTAL_HEIGHT }}
          >
            <TimeAxis />
            {days.map((day) => (
              <DayColumn
                key={day.toISOString()}
                day={day}
                events={timedEvents.filter((e) => eventOverlapsDay(e, day))}
                now={now}
                hoverMinutes={
                  dragHover && dragHover.dayISO === day.toISOString()
                    ? dragHover.minutes
                    : null
                }
                onHover={(minutes) =>
                  setDragHover({ dayISO: day.toISOString(), minutes })
                }
                onLeave={() => setDragHover(null)}
                onDrop={(eventId, sourceISO, minutesFromMidnight) => {
                  setDragHover(null);
                  const target = startOfDay(day);
                  target.setMinutes(minutesFromMidnight);
                  onDropToTime(eventId, sourceISO, target);
                }}
                onResize={onResize}
                onResizeStart={onResizeStart}
                onCreateAt={(minutesFromMidnight) => {
                  const target = startOfDay(day);
                  target.setMinutes(minutesFromMidnight);
                  const end = new Date(target.getTime() + 30 * 60_000);
                  onCreateAt(target, end);
                }}
              />
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ============================================================================
// All-day row
// ============================================================================

const ALL_DAY_ROW_HEIGHT = 24; // px per row of stacked all-day events
const ALL_DAY_ROW_GAP = 2; // px

interface AllDayRowProps<E extends CalendarEventLite> {
  days: Date[];
  events: E[];
  gridTemplateColumns: string;
  onCreateAtDay: (day: Date) => void;
}

function AllDayRow<E extends CalendarEventLite>({
  days,
  events,
  gridTemplateColumns,
  onCreateAtDay,
}: AllDayRowProps<E>): JSX.Element {
  const { placements, rowCount } = useMemo(
    () => packAllDayLanes(events, days),
    [events, days],
  );
  const containerHeight = Math.max(
    ALL_DAY_ROW_HEIGHT,
    rowCount * (ALL_DAY_ROW_HEIGHT + ALL_DAY_ROW_GAP) - ALL_DAY_ROW_GAP,
  );

  return (
    <div
      className="grid border-b border-border bg-surface"
      style={{ gridTemplateColumns }}
    >
      <div className="text-[10px] uppercase tracking-wide text-ink-muted text-right pr-2 pt-1.5">
        all day
      </div>
      <div
        className="relative col-span-full pl-0 cursor-pointer hover:bg-surface-alt/30"
        style={{
          height: containerHeight + 8, // padding
          gridColumn: `2 / span ${days.length}`,
          padding: '4px 0',
        }}
        onClick={(e) => {
          // Clicks on chips stop propagation; only clicks on empty row
          // area reach here. Compute which day column was clicked.
          const rect = e.currentTarget.getBoundingClientRect();
          const offsetX = e.clientX - rect.left;
          const col = Math.max(0, Math.min(days.length - 1, Math.floor((offsetX / rect.width) * days.length)));
          onCreateAtDay(days[col]!);
        }}
      >
        {events.map((e) => {
          const p = placements.get(e.id);
          if (!p) return null;
          const widthPct = (p.spanCol / days.length) * 100;
          const leftPct = (p.startCol / days.length) * 100;
          const top = 4 + p.row * (ALL_DAY_ROW_HEIGHT + ALL_DAY_ROW_GAP);
          return (
            <AllDayChip
              key={e.id}
              event={e}
              top={top}
              leftPct={leftPct}
              widthPct={widthPct}
            />
          );
        })}
      </div>
    </div>
  );
}

function AllDayChip<E extends CalendarEventLite>({
  event,
  top,
  leftPct,
  widthPct,
}: {
  event: E;
  top: number;
  leftPct: number;
  widthPct: number;
}): JSX.Element {
  const tone = STATUS_TONE[event.status] ?? STATUS_TONE['pending']!;
  return (
    <Link
      to={`/events/${event.id}`}
      onClick={(e) => e.stopPropagation()}
      title={`${event.title} · ${formatDateRange(event.start_at, event.end_at)}`}
      className={`absolute rounded border-l-4 px-2 text-[11px] font-medium leading-[20px] truncate hover:ring-1 hover:ring-current ${tone}`}
      style={{
        top,
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        height: ALL_DAY_ROW_HEIGHT,
      }}
    >
      {event.title}
    </Link>
  );
}

// ============================================================================
// Time axis (hour labels)
// ============================================================================

function TimeAxis(): JSX.Element {
  return (
    <div className="border-r border-border">
      {Array.from({ length: 24 }, (_, h) => (
        <div
          key={h}
          className="text-[10px] text-ink-muted text-right pr-2 leading-none"
          style={{ height: HOUR_HEIGHT }}
        >
          {h === 0 ? '' : `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Day column — hour grid lines, drop target, event blocks
// ============================================================================

interface DayColumnProps<E extends CalendarEventLite> {
  day: Date;
  events: E[];
  now: Date;
  hoverMinutes: number | null;
  onHover: (minutes: number) => void;
  onLeave: () => void;
  onDrop: (eventId: string, sourceISO: string, minutesFromMidnight: number) => void;
  onResize: (eventId: string, newEnd: Date) => void;
  onResizeStart: (eventId: string, newStart: Date) => void;
  onCreateAt: (minutesFromMidnight: number) => void;
}

function DayColumn<E extends CalendarEventLite>({
  day,
  events,
  now,
  hoverMinutes,
  onHover,
  onLeave,
  onDrop,
  onResize,
  onResizeStart,
  onCreateAt,
}: DayColumnProps<E>): JSX.Element {
  const isToday = isSameDay(day, startOfDay(now));
  const dayStart = useMemo(() => startOfDay(day), [day]);
  const lanes = useMemo(() => assignLanes(events), [events]);

  return (
    <div
      className="relative border-l border-border cursor-pointer hover:bg-surface-alt/30"
      onClick={(e) => {
        // Clicks on chips or the resize handle stop propagation; only empty
        // grid area reaches here. Compute the snapped time from clientY.
        const minutes = clientYToSnappedMinutes(e.clientY, e.currentTarget);
        onCreateAt(minutes);
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const minutes = clientYToSnappedMinutes(e.clientY, e.currentTarget);
        onHover(minutes);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        onLeave();
      }}
      onDrop={(e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData(DRAG_MIME);
        if (!raw) return;
        try {
          const payload = JSON.parse(raw) as DragPayload;
          const minutes = clientYToSnappedMinutes(e.clientY, e.currentTarget);
          onDrop(payload.id, payload.start_at, minutes);
        } catch {
          /* not our drag */
        }
      }}
    >
      {/* Hour grid lines as background */}
      {Array.from({ length: 24 }, (_, h) => (
        <div
          key={h}
          className="border-t border-border"
          style={{ height: HOUR_HEIGHT }}
        />
      ))}

      {/* Now-line on today */}
      {isToday && <NowLine now={now} dayStart={dayStart} />}

      {/* Drop preview line */}
      {hoverMinutes != null && (
        <div
          className="absolute left-0 right-0 border-t-2 border-accent pointer-events-none z-20"
          style={{ top: (hoverMinutes / 60) * HOUR_HEIGHT }}
        />
      )}

      {/* Event blocks */}
      {events.map((e) => {
        const lane = lanes.get(e.id) ?? { lane: 0, lanes: 1 };
        return (
          <EventBlock
            key={e.id}
            event={e}
            dayStart={dayStart}
            lane={lane.lane}
            lanes={lane.lanes}
            onResize={onResize}
            onResizeStart={onResizeStart}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Now line
// ============================================================================

function NowLine({ now, dayStart }: { now: Date; dayStart: Date }): JSX.Element {
  const minutes = (now.getTime() - dayStart.getTime()) / 60_000;
  return (
    <div
      className="absolute left-0 right-0 border-t-2 border-danger pointer-events-none z-20"
      style={{ top: (minutes / 60) * HOUR_HEIGHT }}
      aria-label="Current time"
    >
      <div className="absolute -top-[5px] -left-[5px] w-2.5 h-2.5 rounded-full bg-danger" />
    </div>
  );
}

// ============================================================================
// Event block — positioned, draggable, resizable
// ============================================================================

interface EventBlockProps<E extends CalendarEventLite> {
  event: E;
  dayStart: Date;
  lane: number;
  lanes: number;
  onResize: (eventId: string, newEnd: Date) => void;
  onResizeStart: (eventId: string, newStart: Date) => void;
}

function EventBlock<E extends CalendarEventLite>({
  event,
  dayStart,
  lane,
  lanes,
  onResize,
  onResizeStart,
}: EventBlockProps<E>): JSX.Element {
  // Independent deltas for top and bottom edges. Only one is non-zero at a
  // time (a single resize gesture is active or none), but tracking them
  // separately keeps the math symmetric and lets `top + height` recalc
  // naturally from both sides.
  const [resizingTopDeltaMin, setResizingTopDeltaMin] = useState(0);
  const [resizingDeltaMin, setResizingDeltaMin] = useState(0);

  const startMin = (new Date(event.start_at).getTime() - dayStart.getTime()) / 60_000;
  const endMin = (new Date(event.end_at).getTime() - dayStart.getTime()) / 60_000;
  const clampedStart = Math.max(0, startMin + resizingTopDeltaMin);
  const clampedEnd = Math.min(24 * 60, endMin + resizingDeltaMin);
  const top = (clampedStart / 60) * HOUR_HEIGHT;
  const heightPx = Math.max(20, ((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT);

  const widthPct = 100 / lanes;
  const leftPct = lane * widthPct;

  const tone = STATUS_TONE[event.status] ?? STATUS_TONE['pending']!;

  const onResizeMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const initialEnd = new Date(event.end_at);
    const minEnd = new Date(new Date(event.start_at).getTime() + MIN_DURATION_MIN * 60_000);

    let lastDeltaMin = 0;

    const move = (ev: MouseEvent): void => {
      const dy = ev.clientY - startY;
      const rawMin = (dy / HOUR_HEIGHT) * 60;
      let snapped = Math.round(rawMin / SNAP_MIN) * SNAP_MIN;
      const candidateEnd = new Date(initialEnd.getTime() + snapped * 60_000);
      if (candidateEnd < minEnd) {
        snapped = Math.round((minEnd.getTime() - initialEnd.getTime()) / 60_000);
      }
      lastDeltaMin = snapped;
      setResizingDeltaMin(snapped);
    };
    const up = (): void => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      setResizingDeltaMin(0);
      if (lastDeltaMin === 0) return;
      onResize(event.id, new Date(initialEnd.getTime() + lastDeltaMin * 60_000));
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const onResizeTopMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const initialStart = new Date(event.start_at);
    // start_at can move up to end_at - MIN_DURATION (drag-down to shorten);
    // dragging up makes it earlier with no lower bound beyond 24:00 clamp
    // on the visual side (the server has no past-time restriction).
    const maxStart = new Date(new Date(event.end_at).getTime() - MIN_DURATION_MIN * 60_000);

    let lastDeltaMin = 0;

    const move = (ev: MouseEvent): void => {
      const dy = ev.clientY - startY;
      const rawMin = (dy / HOUR_HEIGHT) * 60;
      let snapped = Math.round(rawMin / SNAP_MIN) * SNAP_MIN;
      const candidateStart = new Date(initialStart.getTime() + snapped * 60_000);
      if (candidateStart > maxStart) {
        snapped = Math.round((maxStart.getTime() - initialStart.getTime()) / 60_000);
      }
      lastDeltaMin = snapped;
      setResizingTopDeltaMin(snapped);
    };
    const up = (): void => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      setResizingTopDeltaMin(0);
      if (lastDeltaMin === 0) return;
      onResizeStart(event.id, new Date(initialStart.getTime() + lastDeltaMin * 60_000));
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const isVisuallyTall = heightPx >= 40;

  return (
    <div
      className="absolute z-10 px-0.5"
      style={{ top, height: heightPx, left: `${leftPct}%`, width: `${widthPct}%` }}
    >
      {/* Top resize handle: mirror of the bottom one */}
      <div
        onMouseDown={onResizeTopMouseDown}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 left-1 right-1 h-1.5 cursor-ns-resize z-20 group"
        title="Drag to resize start time"
      >
        <div className="h-full opacity-0 group-hover:opacity-50 bg-current rounded-full" />
      </div>
      <Link
        to={`/events/${event.id}`}
        draggable
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          const payload: DragPayload = { id: event.id, start_at: event.start_at };
          e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
          e.dataTransfer.setData('text/plain', event.title);
        }}
        title={`${formatTime(new Date(event.start_at))} – ${formatTime(new Date(event.end_at))}: ${event.title}`}
        className={`block h-full rounded border-l-4 px-1.5 py-0.5 text-[11px] overflow-hidden cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-current ${tone}`}
      >
        <div className="font-medium truncate leading-tight">{event.title}</div>
        {isVisuallyTall && (
          <div className="opacity-75 truncate text-[10px] mt-0.5">
            {formatTime(new Date(event.start_at))}
          </div>
        )}
      </Link>
      {/* Resize handle: ~6px tall strip at the bottom edge */}
      <div
        onMouseDown={onResizeMouseDown}
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-0 left-1 right-1 h-1.5 cursor-ns-resize z-20 group"
        title="Drag to resize"
      >
        <div className="h-full opacity-0 group-hover:opacity-50 bg-current rounded-full" />
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function eventOverlapsDay(event: CalendarEvent, day: Date): boolean {
  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  return new Date(event.start_at) < dayEnd && new Date(event.end_at) > dayStart;
}

function clientYToSnappedMinutes(clientY: number, target: HTMLElement): number {
  // The column itself doesn't scroll (its parent does), so getBoundingClientRect
  // already accounts for the parent's scroll position. clientY - rect.top gives
  // the position within the visible column.
  const rect = target.getBoundingClientRect();
  const offsetY = clientY - rect.top;
  const rawMin = (offsetY / HOUR_HEIGHT) * 60;
  const snapped = Math.round(rawMin / SNAP_MIN) * SNAP_MIN;
  return Math.max(0, Math.min(24 * 60 - SNAP_MIN, snapped));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * For each all-day event, find its visible column range (clamped to the
 * days array) and pack into vertical lanes such that no two events in the
 * same lane overlap horizontally.
 *
 * Greedy: events sorted by startCol (then by spanCol descending so longer
 * bars come first within a day). For each, find the first lane whose
 * last-placed event ends at or before the new event's startCol.
 */
function packAllDayLanes<E extends CalendarEventLite>(
  events: E[],
  days: Date[],
): {
  placements: Map<string, { row: number; startCol: number; spanCol: number }>;
  rowCount: number;
} {
  const placements = new Map<string, { row: number; startCol: number; spanCol: number }>();
  if (events.length === 0 || days.length === 0) return { placements, rowCount: 0 };

  const firstDayStart = startOfDay(days[0]!).getTime();
  const lastDayEnd = startOfDay(days[days.length - 1]!).getTime() + DAY_MS;

  const visible = events
    .map((e) => {
      const eStart = new Date(e.start_at).getTime();
      const eEnd = new Date(e.end_at).getTime();
      if (eEnd <= firstDayStart || eStart >= lastDayEnd) return null;
      let startCol = 0;
      for (let i = 0; i < days.length; i += 1) {
        const dayEnd = startOfDay(days[i]!).getTime() + DAY_MS;
        if (eStart < dayEnd) {
          startCol = i;
          break;
        }
      }
      let endCol = days.length - 1;
      for (let i = days.length - 1; i >= 0; i -= 1) {
        const dayStart = startOfDay(days[i]!).getTime();
        if (eEnd > dayStart) {
          endCol = i;
          break;
        }
      }
      return { event: e, startCol, spanCol: endCol - startCol + 1 };
    })
    .filter((x): x is { event: E; startCol: number; spanCol: number } => x !== null)
    .sort((a, b) => a.startCol - b.startCol || b.spanCol - a.spanCol);

  const lanesNextFreeCol: number[] = [];
  for (const item of visible) {
    let row = -1;
    for (let r = 0; r < lanesNextFreeCol.length; r += 1) {
      if (lanesNextFreeCol[r]! <= item.startCol) {
        lanesNextFreeCol[r] = item.startCol + item.spanCol;
        row = r;
        break;
      }
    }
    if (row === -1) {
      row = lanesNextFreeCol.length;
      lanesNextFreeCol.push(item.startCol + item.spanCol);
    }
    placements.set(item.event.id, { row, startCol: item.startCol, spanCol: item.spanCol });
  }
  return { placements, rowCount: lanesNextFreeCol.length };
}

function formatDateRange(startISO: string, endISO: string): string {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const sameDay = start.toDateString() === end.toDateString();
  const fmt = (d: Date): string => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return sameDay ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}
